import React, { useState, useEffect, useCallback } from 'react';
import { notificationApi } from '../../services/api';
import { useAppStore } from '../../store/appStore';
import { formatDistanceToNow } from 'date-fns';

const TYPE_META = {
  info:    { label: 'Info',        color: 'var(--accent-cyan)' },
  feature: { label: 'New Feature', color: 'var(--accent-green)' },
  warning: { label: 'Important',   color: 'var(--accent-orange)' },
};

export default function NotificationsPanel() {
  const { user, addToast, setUnreadNotifications } = useAppStore();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = useCallback(async (markRead = false) => {
    try {
      const res = await notificationApi.list();
      setItems(res.data.notifications || []);
      // Opening the center marks everything read and clears the badge.
      if (markRead) {
        await notificationApi.markAllRead();
        setUnreadNotifications(0);
      }
    } catch {
      addToast({ type: 'error', message: 'Failed to load notifications' });
    } finally {
      setLoading(false);
    }
  }, [addToast, setUnreadNotifications]);

  // Mark read on first open of the panel.
  useEffect(() => { load(true); }, [load]);

  async function handleDelete(id) {
    if (!window.confirm('Delete this notification for everyone?')) return;
    try {
      await notificationApi.delete(id);
      setItems(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to delete' });
    }
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '16px', gap: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, gap: '10px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--accent-cyan)', textTransform: 'uppercase' }}>
          Notifications
        </div>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Notification
          </button>
        )}
      </div>

      <div className="scrollable" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {loading ? (
          <div style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '12px', padding: '20px', textAlign: 'center' }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)', fontSize: '13px', padding: '40px', textAlign: 'center' }}>
            No notifications yet.
          </div>
        ) : items.map(n => {
          const meta = TYPE_META[n.type] || TYPE_META.info;
          return (
            <div key={n.id} style={{
              position: 'relative',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-dim)',
              borderLeft: `3px solid ${meta.color}`,
              borderRadius: 'var(--radius-md)',
              padding: '14px 16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: meta.color, border: `1px solid ${meta.color}`, borderRadius: '999px', padding: '2px 8px' }}>
                  {meta.label}
                </span>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {n.title}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)' }}>
                  {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                  {n.updatedAt && <span style={{ marginLeft: '6px', fontStyle: 'italic', opacity: 0.8 }}>· edited</span>}
                </span>
                {isAdmin && (
                  <button onClick={() => setEditTarget(n)} title="Edit notification" style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px', display: 'flex' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-cyan)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                )}
                {isAdmin && (
                  <button onClick={() => handleDelete(n.id)} title="Delete notification" style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px', display: 'flex' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-red)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                  </button>
                )}
              </div>
              {n.message && (
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '13px', lineHeight: 1.5, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {n.message}
                </div>
              )}
              {n.createdByName && (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', marginTop: '8px' }}>
                  — {n.createdByName}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(showCreate || editTarget) && (
        <NotificationModal
          notification={editTarget}
          onClose={() => { setShowCreate(false); setEditTarget(null); }}
          onSaved={async () => { setShowCreate(false); setEditTarget(null); await load(); }}
        />
      )}
    </div>
  );
}

function NotificationModal({ notification, onClose, onSaved }) {
  const isEdit = !!notification;
  const { addToast } = useAppStore();
  const [title, setTitle] = useState(notification?.title || '');
  const [message, setMessage] = useState(notification?.message || '');
  const [type, setType] = useState(notification?.type || 'info');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim()) { addToast({ type: 'error', message: 'Title is required' }); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await notificationApi.update(notification.id, { title, message, type });
        addToast({ type: 'success', message: 'Notification updated' });
      } else {
        await notificationApi.create({ title, message, type });
        addToast({ type: 'success', message: 'Notification published' });
      }
      onSaved();
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || `Failed to ${isEdit ? 'update' : 'create'} notification` });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Notification' : 'New Notification'}</h2>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label className="label">Type</label>
            <select className="input-field select-field" value={type} onChange={e => setType(e.target.value)}>
              <option value="info">Info</option>
              <option value="feature">New Feature</option>
              <option value="warning">Important</option>
            </select>
          </div>
          <div>
            <label className="label">Title</label>
            <input className="input-field" value={title} onChange={e => setTitle(e.target.value)} maxLength={200} placeholder="e.g. New: Live operator chat" required />
          </div>
          <div>
            <label className="label">Message</label>
            <textarea className="input-field" value={message} onChange={e => setMessage(e.target.value)} maxLength={4000} rows={5} placeholder="Describe the update for your users…" style={{ resize: 'vertical', fontFamily: 'var(--font-display)' }} />
          </div>
          <div className="modal-footer" style={{ padding: 0 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Publish')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
